/**
 * The one decision the version check makes, as a function with no fetch in it.
 *
 * Split out for the reason `ErrorBoundary.logic.ts` and `combobox.logic.ts` are:
 * this console has no jsdom, so anything that can only be exercised by mounting
 * a component or stubbing the network is in practice untested. What is worth
 * testing here is the classification — everything else is plumbing around it.
 */

/** Where the last version-triggered reload is recorded. */
export const VERSION_RELOAD_MARK = 'fv:version-reload';

/**
 * How long a tab may go without asking whether it is current.
 *
 * The check runs when the tab becomes visible rather than on a timer, so this is
 * a floor on how often that can cost a request, not a polling interval. Five
 * minutes means a manager clicking between two tabs all afternoon asks about
 * twelve times an hour at worst, for a fifty-byte file.
 */
export const VERSION_CHECK_INTERVAL_MS = 5 * 60_000;

/**
 * Whether `/version.json` describes a build other than the one running.
 *
 * FALSE FOR EVERY SHAPE THAT IS NOT AN ANSWER, and that is the whole of the
 * care taken here. This function decides whether to put a banner in front of a
 * manager and eventually reload their page, on evidence fetched from a URL that
 * can legitimately return several things that are not a version:
 *
 *   - a 404 body, in dev and in any container built before this shipped;
 *   - `index.html`, if the SPA fallback ever catches this path;
 *   - a truncated or empty response from a connection that dropped.
 *
 * A false positive is a console that nags about an update that does not exist
 * and reloads itself on every navigation. A false negative costs one missed
 * deploy notice, and the ErrorBoundary still catches the stale-chunk case. So
 * anything not recognisably `{ build: <non-empty string> }` is read as "no
 * information" rather than as "you are out of date".
 *
 * A build id that MATCHES is also false, obviously — but note it is compared as
 * an opaque string. There is no ordering: an id is not newer or older, only the
 * same or different. A rollback is a change too, and reloading into it is right.
 */
export function isNewBuild(current: string, payload: unknown): boolean {
  if (typeof payload !== 'object' || payload === null) return false;
  if (!('build' in payload)) return false;

  const { build } = payload as { build: unknown };
  if (typeof build !== 'string' || build === '') return false;

  // An unset `__FV_BUILD__` — a build that somehow skipped the define — would
  // otherwise differ from every published id and reload forever.
  if (current === '') return false;

  return build !== current;
}
